import React, { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MapView from "@/components/MapView";
import CustomDomainPanel from "@/components/CustomDomainPanel";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import AccountingPanel from "@/components/AccountingPanel";
import PricingPlansPanel from "@/components/PricingPlansPanel";
import AddonServicesPanel from "@/components/AddonServicesPanel";
import { PropertiesSubPanel, BinsSubPanel } from "@/components/PropertiesBinsPanel";
import { FleetPanel, ShiftPlannerPanel, TourAutoplanPanel, PaymentsPanel } from "@/components/Phase4Panels";
import { NewsletterPanel, ServiceStatusPanel, SkipReasonsPanel, PromoCodesPanel } from "@/components/Phase4bPanels";
import ReferencesPanel from "@/components/ReferencesPanel";
import LeadsPanel from "@/components/LeadsPanel";
import MasterDataPanel from "@/components/MasterDataPanel";
import OperationsPanel from "@/components/OperationsPanel";
import PricingPanel from "@/components/PricingPanel";
import ServiceZonesPanel from "@/components/ServiceZonesPanel";
import TourMapPanel from "@/components/TourMapPanel";
import RollcardPanel from "@/components/RollcardPanel";
import WasteProvidersPanel from "@/components/WasteProvidersPanel";
import BookkeepingPanel from "@/components/BookkeepingPanel";
import ControllingPanel from "@/components/ControllingPanel";
import GpsHeatmapPanel from "@/components/GpsHeatmapPanel";
import TaxAdvisorsPanel from "@/components/TaxAdvisorsPanel";
import CustomerPricingSubPanel from "@/components/CustomerPricingSubPanel";
import SmtpSettingsPanel from "@/components/SmtpSettingsPanel";
import usePageTitle from "@/lib/usePageTitle";
import { Recycle, SignOut, Users, Trash, Receipt, MapPin, Plus, Truck, UserCircle, PencilSimple, X, ArrowSquareOut, MagnifyingGlass, FileXls, FilePdf, FileCsv, Bell } from "@phosphor-icons/react";

export default function TenantApp() {
  const { user, logout, loading } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [bins, setBins] = useState([]);
  const [subs, setSubs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState("overview");
  usePageTitle(tenant ? "Backoffice" : "Backoffice", tenant?.name);

  // Tab-Wechsel via CustomEvent (z.B. nach Lead-Konvertierung)
  useEffect(() => {
    const handler = (e) => {
      // Kunden-Tab öffnen und danach das Event weiterreichen, damit CustomersPanel
      // (das erst nach dem Tab-Wechsel gerendert wird) den passenden Kunden öffnet.
      setTab("customers");
      // Nach dem Refresh der Kundenliste erneut dispatchen — kleiner Delay,
      // damit CustomersPanel gemountet ist und den Handler registriert hat.
      const cid = e.detail?.customer_id;
      if (!cid) return;
      // Refresh + re-dispatch
      (async () => {
        try {
          const r = await api.get("/customers");
          setCustomers(r.data);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("tonneva:open-customer", { detail: { customer_id: cid } }));
          }, 200);
        } catch (e) { console.warn("[TenantApp] handler failed", e); }
      })();
    };
    window.addEventListener("tonneva:open-customer-from-lead", handler);
    return () => window.removeEventListener("tonneva:open-customer-from-lead", handler);
  }, []);

  useEffect(() => {
    if (!user || !user.tenant_id) return;
    (async () => {
      const safe = (p) => p.then((r) => r.data).catch((e) => { console.warn("fetch failed", e); return null; });
      const [t, m, c, b, s, j] = await Promise.all([
        safe(api.get("/tenants/me")),
        safe(api.get("/metrics/tenant")),
        safe(api.get("/customers")),
        safe(api.get("/bins")),
        safe(api.get("/subscriptions")),
        safe(api.get("/jobs")),
      ]);
      if (t) setTenant(t);
      if (m) setMetrics(m);
      if (c) setCustomers(c);
      if (b) setBins(b);
      if (s) setSubs(s);
      if (j) setJobs(j);
    })();
  }, [user]);

  const stops = useMemo(() => customers
    .filter((c) => c.location?.lat && c.location?.lng)
    .map((c) => ({
      lng: c.location.lng,
      lat: c.location.lat,
      label: `${c.first_name} ${c.last_name} — ${c.location.street}`,
    })), [customers]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.tenant_id) return <Navigate to="/me" replace />;

  const refresh = async () => {
    const [c, b, s, j, m] = await Promise.all([api.get("/customers"), api.get("/bins"), api.get("/subscriptions"), api.get("/jobs"), api.get("/metrics/tenant")]);
    setCustomers(c.data); setBins(b.data); setSubs(s.data); setJobs(j.data); setMetrics(m.data);
  };

  return (
    <div className="min-h-screen bg-zinc-50 overflow-x-hidden">
      <header className="bg-white border-b border-[var(--tonneva-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-14 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/brand/tonneva-logo.png" alt="Tonneva" className="w-7 h-7" />
            <div>
              <div className="font-heading font-bold tracking-tight text-sm">{tenant?.name || "Workspace"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--tonneva-primary)" }}>Tonneva · Backoffice</div>
            </div>
            {tenant?.branding?.primary_color && (
              <span className="ml-3 w-4 h-4 rounded-sm border border-black/10" title="Mandanten-CI" style={{ background: tenant.branding.primary_color }} />
            )}
          </div>
          <div className="flex items-center gap-x-3 gap-y-1 text-xs flex-wrap justify-end">
            {tenant?.slug && (
              <a
                href={`/portal/${tenant.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-black/10 text-zinc-700 hover:bg-zinc-50 hover:border-black/20 transition font-medium"
                data-testid="open-portal-link"
                title="Ihre öffentliche Portal-/Landingpage in neuem Tab öffnen"
              >
                <ArrowSquareOut size={13} weight="bold" /> Portal öffnen
              </a>
            )}
            <span className="hidden md:inline text-zinc-500">{user.email} · <span className="font-mono uppercase">{user.role.replace("tenant_", "")}</span></span>
            <NotificationBell onNavigate={setTab} />
            <ChangePasswordDialog className="text-zinc-600 hover:text-[var(--tonneva-primary)]" />
            <button onClick={logout} className="flex items-center gap-1.5 text-zinc-600 hover:text-[var(--tonneva-primary)]" data-testid="logout-btn">
              <SignOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard icon={<Users size={14} weight="bold" />} label="Kunden" value={metrics?.customers ?? "—"} />
          <MetricCard icon={<Trash size={14} weight="bold" />} label="Tonnen" value={metrics?.bins ?? "—"} />
          <MetricCard icon={<Recycle size={14} weight="bold" />} label="Aktive Abos" value={metrics?.active_subscriptions ?? "—"} />
          <MetricCard icon={<Truck size={14} weight="bold" />} label="Offene Jobs" value={metrics?.planned_jobs ?? "—"} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-[var(--tonneva-border)] rounded-full p-1 h-auto flex flex-nowrap overflow-x-auto md:flex-wrap justify-start [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>button]:shrink-0 [&>button]:whitespace-nowrap">
            <TabsTrigger value="overview" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-overview">Übersicht</TabsTrigger>
            <TabsTrigger value="customers" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-customers">Kunden</TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-subscriptions">Abos</TabsTrigger>
            <TabsTrigger value="routes" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-routes">Touren</TabsTrigger>
            <TabsTrigger value="fleet" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-fleet">Fuhrpark</TabsTrigger>
            <TabsTrigger value="shifts" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-shifts">Schichten</TabsTrigger>
            <TabsTrigger value="billing" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-billing">Abrechnung</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-payments">Zahlungen</TabsTrigger>
            <TabsTrigger value="newsletter" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-pricing">Preise</TabsTrigger>
            <TabsTrigger value="vas" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-vas">VAS</TabsTrigger>
            <TabsTrigger value="team" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-team">Team</TabsTrigger>
            <TabsTrigger value="branding" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-branding">Branding</TabsTrigger>
            <TabsTrigger value="email" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-email">E-Mail</TabsTrigger>
            <TabsTrigger value="references" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-references">Referenzen</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-leads">Interessenten</TabsTrigger>
            <TabsTrigger value="masterdata" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-masterdata">Stammdaten</TabsTrigger>
            <TabsTrigger value="operations" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-operations">Betrieb</TabsTrigger>
            <TabsTrigger value="pricing-rules" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-pricing-rules">Zuschläge</TabsTrigger>
            <TabsTrigger value="zones" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-zones">Bezirke</TabsTrigger>
            <TabsTrigger value="tour-map" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-tour-map">Tour-Karte</TabsTrigger>
            <TabsTrigger value="gps" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-gps">GPS-Analyse</TabsTrigger>
            <TabsTrigger value="bookkeeping" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-bookkeeping">Gutschriften</TabsTrigger>
            <TabsTrigger value="controlling" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white text-zinc-700 px-4 py-1.5 text-xs font-medium" data-testid="tab-controlling">Controlling</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 bg-white border border-black/10 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2"><MapPin size={16} weight="bold" /> Kunden-Standorte</h3>
                  <span className="text-xs text-zinc-500">{stops.length} Marker</span>
                </div>
                <MapView stops={stops} height="380px" />
              </div>
              <div className="bg-white border border-black/10 p-5">
                <h3 className="font-heading text-lg font-semibold tracking-tight mb-3">Schnellaktion</h3>
                <p className="text-sm text-zinc-500 mb-4">Teilen Sie diesen Link mit Ihren Kunden:</p>
                {tenant && (
                  <div className="bg-zinc-50 border border-black/10 p-3 font-mono text-xs break-all" data-testid="portal-link">
                    {window.location.origin}/portal/{tenant.slug}
                  </div>
                )}
                <Button className="w-full mt-4 rounded-none bg-black text-white hover:bg-zinc-800" onClick={() => window.open(`/portal/${tenant.slug}`, "_blank")} data-testid="open-portal-btn">
                  Portal öffnen
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            <CustomersPanel customers={customers} onChange={refresh} tenant={tenant} />
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-6">
            <SubsPanel subs={subs} customers={customers} onChange={refresh} tenant={tenant} />
          </TabsContent>

          <TabsContent value="routes" className="mt-6">
            <div className="space-y-6">
              <TourAutoplanPanel onGenerated={refresh} />
              <RoutesPanel jobs={jobs} customers={customers} onChange={refresh} />
            </div>
          </TabsContent>

          <TabsContent value="fleet" className="mt-6">
            <div className="space-y-6">
              <FleetPanel />
              <SkipReasonsPanel />
            </div>
          </TabsContent>

          <TabsContent value="shifts" className="mt-6">
            <ShiftPlannerPanel />
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            <PaymentsPanel customers={customers} onChange={refresh} />
          </TabsContent>

          <TabsContent value="newsletter" className="mt-6">
            <div className="space-y-6">
              <PromoCodesPanel />
              <NewsletterPanel tenant={tenant} />
            </div>
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <BillingPanel customers={customers} onChange={refresh} tenant={tenant} onTenantChange={(t) => setTenant(t)} />
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <PricingPlansPanel tenant={tenant} onSaved={(t) => setTenant(t)} />
          </TabsContent>

          <TabsContent value="vas" className="mt-6">
            <AddonServicesPanel tenant={tenant} />
          </TabsContent>

          <TabsContent value="team" className="mt-6">
            <TeamPanel currentUser={user} />
          </TabsContent>

          <TabsContent value="branding" className="mt-6">
            <div className="space-y-6">
              <ServiceStatusPanel tenant={tenant} onSaved={(t) => setTenant(t)} />
              <BrandingPanel tenant={tenant} onSaved={(t) => setTenant(t)} />
              <div>
                <CustomDomainPanel tenant={tenant} onSaved={(t) => setTenant(t)} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="email" className="mt-6">
            <SmtpSettingsPanel endpoint="/tenant/smtp-settings" />
          </TabsContent>

          <TabsContent value="references" className="mt-6">
            <ReferencesPanel tenant={tenant} />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <LeadsPanel tenant={tenant} onCustomerCreated={() => refresh()} onTenantChanged={(t) => setTenant(t)} />
          </TabsContent>

          <TabsContent value="masterdata" className="mt-6">
            <MasterDataPanel tenant={tenant} onSaved={(t) => setTenant(t)} />
          </TabsContent>

          <TabsContent value="operations" className="mt-6">
            <OperationsPanel tenant={tenant} />
          </TabsContent>

          <TabsContent value="pricing-rules" className="mt-6">
            <PricingPanel tenant={tenant} />
          </TabsContent>

          <TabsContent value="zones" className="mt-6">
            <ServiceZonesPanel tenant={tenant} />
          </TabsContent>

          <TabsContent value="tour-map" className="mt-6 space-y-6">
            <TourMapPanel tenant={tenant} />
            <RollcardPanel />
            <WasteProvidersPanel />
          </TabsContent>

          <TabsContent value="gps" className="mt-6">
            <GpsHeatmapPanel />
          </TabsContent>

          <TabsContent value="bookkeeping" className="mt-6">
            <BookkeepingPanel tenant={tenant} />
          </TabsContent>

          <TabsContent value="controlling" className="mt-6 space-y-6">
            <ControllingPanel />
            <TaxAdvisorsPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const MetricCard = ({ icon, label, value }) => (
  <div className="bg-white border border-black/10 p-4">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{icon} {label}</div>
    <div className="font-mono text-2xl font-medium tracking-tight">{value}</div>
  </div>
);

function CustomersPanel({ customers, onChange, tenant }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null); // customer being viewed
  // Reagiere auf Öffnen-Events z.B. nach Lead-Konvertierung
  useEffect(() => {
    const handler = (e) => {
      const cid = e.detail?.customer_id;
      if (!cid) return;
      const found = customers.find((c) => c.customer_id === cid);
      if (found) setDetail(found);
      else {
        // customer nicht in aktueller Liste — hole ihn nach und öffne
        onChange && onChange();
      }
    };
    window.addEventListener("tonneva:open-customer", handler);
    return () => window.removeEventListener("tonneva:open-customer", handler);
  }, [customers, onChange]);
  const [f, setF] = useState({ type: "private", first_name: "", last_name: "", company_name: "", contact_person: "", email: "", phone: "", street: "", zip: "", city: "", internal_notes: "", driver_notes: "", payment_method: "invoice", prepaid_balance_eur: 0, registered_via: "phone", promo_code: "" });
  const [promoStatus, setPromoStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [exporting, setExporting] = useState(null);

  const q = search.trim().toLowerCase();
  const filtered = customers.filter((c) => {
    if (typeFilter !== "all" && c.type !== typeFilter) return false;
    if (!q) return true;
    const haystack = [
      c.customer_number, c.first_name, c.last_name, c.company_name, c.contact_person,
      c.email, c.phone, c.location?.street, c.location?.zip, c.location?.city, c.internal_notes,
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(q);
  });

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (typeFilter !== "all") params.set("type", typeFilter);
      const url = `${backend}/api/exports/customers.${format}${params.toString() ? "?" + params.toString() : ""}`;
      // Same-origin fetch with cookie auth, then trigger download
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const disp = r.headers.get("content-disposition") || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `kunden.${format}`;
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      alert(`Export fehlgeschlagen: ${e.message}`);
    } finally {
      setExporting(null);
    }
  };

  const checkPromo = async (code) => {
    if (!code || !tenant?.slug) { setPromoStatus(null); return; }
    try {
      const r = await api.post("/promos/validate", { tenant_slug: tenant.slug, code });
      setPromoStatus(r.data);
    } catch { setPromoStatus({ valid: false, reason: "error" }); }
  };

  const create = async () => {
    try {
      await api.post("/customers", {
        type: f.type, first_name: f.first_name, last_name: f.last_name,
        company_name: f.company_name, contact_person: f.contact_person,
        email: f.email, phone: f.phone,
        location: { street: f.street, zip: f.zip, city: f.city },
        internal_notes: f.internal_notes, driver_notes: f.driver_notes,
        payment_method: f.payment_method,
        prepaid_balance_cents: Math.round((+f.prepaid_balance_eur || 0) * 100),
        registered_via: f.registered_via,
        promo_code: promoStatus?.valid ? f.promo_code : null,
      });
      setOpen(false);
      setF({ type: "private", first_name: "", last_name: "", company_name: "", contact_person: "", email: "", phone: "", street: "", zip: "", city: "", internal_notes: "", driver_notes: "", payment_method: "invoice", prepaid_balance_eur: 0, registered_via: "phone", promo_code: "" });
      setPromoStatus(null);
      onChange();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="bg-white border border-black/10">
      <div className="px-5 py-3 border-b border-black/10 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-heading text-lg font-semibold tracking-tight">
            Kunden <span className="text-zinc-400 font-normal">({filtered.length}{filtered.length !== customers.length && ` / ${customers.length}`})</span>
          </h3>
          <div className="relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              className="h-9 rounded-none pl-8 w-64 text-sm"
              placeholder="Suche Name, PLZ, Ort, E-Mail, Telefon…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="customers-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                data-testid="customers-search-clear"
                aria-label="Suche löschen"
              >×</button>
            )}
          </div>
          <div className="flex gap-1">
            {[
              { k: "all", label: "Alle" },
              { k: "private", label: "Privat" },
              { k: "business", label: "Gewerbe" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTypeFilter(t.k)}
                className={`text-[10px] uppercase tracking-widest px-2.5 py-1.5 border transition ${typeFilter === t.k ? "bg-black text-white border-black" : "bg-white text-zinc-500 border-black/10 hover:border-black/30"}`}
                data-testid={`customers-filter-${t.k}`}
              >{t.label}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Export Dropdown */}
          <div className="flex border border-black/10" data-testid="customers-export-group">
            <button
              onClick={() => exportFile("xlsx")}
              disabled={exporting || filtered.length === 0}
              className="text-xs px-3 h-9 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1.5 border-r border-black/10"
              data-testid="export-xlsx-btn"
              title="Als Excel-Datei exportieren"
            >
              <FileXls size={13} weight="bold" /> {exporting === "xlsx" ? "…" : "Excel"}
            </button>
            <button
              onClick={() => exportFile("pdf")}
              disabled={exporting || filtered.length === 0}
              className="text-xs px-3 h-9 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5 border-r border-black/10"
              data-testid="export-pdf-btn"
              title="Als PDF exportieren"
            >
              <FilePdf size={13} weight="bold" /> {exporting === "pdf" ? "…" : "PDF"}
            </button>
            <button
              onClick={() => exportFile("csv")}
              disabled={exporting || filtered.length === 0}
              className="text-xs px-3 h-9 hover:bg-zinc-100 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="export-csv-btn"
              title="Als CSV exportieren"
            >
              <FileCsv size={13} weight="bold" /> {exporting === "csv" ? "…" : "CSV"}
            </button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="new-customer-btn"><Plus size={12} weight="bold" className="mr-1" /> Neu</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl">Neuer Kunde</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Typ</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger className="rounded-none h-11" data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="private">Privat</SelectItem><SelectItem value="business">Gewerbe</SelectItem></SelectContent>
                </Select>
              </div>
              {f.type === "business" && (
                <>
                  <div className="col-span-2"><Label className="text-xs">Firma</Label><Input className="h-11 rounded-none" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="customer-company" /></div>
                  <div className="col-span-2"><Label className="text-xs">Kontaktperson (Ansprechpartner)</Label><Input className="h-11 rounded-none" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} data-testid="customer-contact-person" /></div>
                </>
              )}
              <div><Label className="text-xs">Vorname</Label><Input className="h-11 rounded-none" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="customer-first" /></div>
              <div><Label className="text-xs">Nachname</Label><Input className="h-11 rounded-none" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="customer-last" /></div>
              <div><Label className="text-xs">E-Mail</Label><Input className="h-11 rounded-none" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="customer-email" /></div>
              <div><Label className="text-xs">Telefon</Label><Input className="h-11 rounded-none" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="customer-phone" /></div>
              <div className="col-span-2"><Label className="text-xs">Straße & Nr.</Label><Input className="h-11 rounded-none" value={f.street} onChange={(e) => setF({ ...f, street: e.target.value })} data-testid="customer-street" /></div>
              <div><Label className="text-xs">PLZ</Label><Input className="h-11 rounded-none" value={f.zip} onChange={(e) => setF({ ...f, zip: e.target.value })} data-testid="customer-zip" /></div>
              <div><Label className="text-xs">Stadt</Label><Input className="h-11 rounded-none" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="customer-city" /></div>
              <div className="col-span-2"><Label className="text-xs">Interne Bemerkung (nur Backoffice sichtbar)</Label><textarea rows="2" className="w-full text-sm p-2 border border-black/10" value={f.internal_notes} onChange={(e) => setF({ ...f, internal_notes: e.target.value })} data-testid="customer-internal-notes" /></div>
              <div className="col-span-2"><Label className="text-xs">Hinweis für Fahrer (im Fahrer-View sichtbar)</Label><textarea rows="2" className="w-full text-sm p-2 border border-black/10" value={f.driver_notes} onChange={(e) => setF({ ...f, driver_notes: e.target.value })} data-testid="customer-driver-notes" /></div>

              <div className="col-span-2 pt-3 border-t border-black/10 mt-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Anmelde-Setup (Telefon / Direkt)</div>
              </div>
              <div>
                <Label className="text-xs">Zahlungsart</Label>
                <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
                  <SelectTrigger className="rounded-none h-11" data-testid="customer-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Rechnung</SelectItem>
                    <SelectItem value="vorkasse">Vorkasse</SelectItem>
                    <SelectItem value="sepa">SEPA-Lastschrift</SelectItem>
                    <SelectItem value="card">Kreditkarte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Angemeldet via</Label>
                <Select value={f.registered_via} onValueChange={(v) => setF({ ...f, registered_via: v })}>
                  <SelectTrigger className="rounded-none h-11" data-testid="customer-registered-via"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Telefonisch</SelectItem>
                    <SelectItem value="portal">Portal</SelectItem>
                    <SelectItem value="walk_in">Persönlich</SelectItem>
                    <SelectItem value="email">E-Mail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {f.payment_method === "vorkasse" && (
                <div className="col-span-2">
                  <Label className="text-xs">Vorkasse-Guthaben initial (EUR)</Label>
                  <Input type="number" step="0.01" min="0" className="h-11 rounded-none" value={f.prepaid_balance_eur} onChange={(e) => setF({ ...f, prepaid_balance_eur: e.target.value })} placeholder="z. B. 50.00 wenn schon überwiesen" data-testid="customer-prepaid" />
                  <div className="text-[11px] text-zinc-500 mt-1">Wenn der Kunde bereits per Überweisung ein Guthaben hinterlegt hat, hier eintragen. Kann später über die Vorkasse-Bestätigung erhöht werden.</div>
                </div>
              )}
              <div className="col-span-2">
                <Label className="text-xs">Promo-Code (optional)</Label>
                <div className="flex gap-2">
                  <Input className="h-11 rounded-none flex-1 uppercase font-mono" value={f.promo_code} onChange={(e) => { const v = e.target.value.toUpperCase(); setF({ ...f, promo_code: v }); }} onBlur={() => checkPromo(f.promo_code)} placeholder="EARLY50" data-testid="customer-promo-code" />
                  <Button type="button" variant="outline" className="h-11 rounded-none text-xs" onClick={() => checkPromo(f.promo_code)}>Prüfen</Button>
                </div>
                {promoStatus && (
                  <div className={`text-xs mt-1 ${promoStatus.valid ? "text-emerald-700" : "text-red-600"}`} data-testid="promo-check-result">
                    {promoStatus.valid
                      ? `✓ ${promoStatus.description || promoStatus.code} — ${promoStatus.discount_percent ? promoStatus.discount_percent + "% Rabatt" : "€" + (promoStatus.discount_cents / 100).toFixed(2) + " Rabatt"}`
                      : `✗ Code ${promoStatus.reason === "unknown_code" ? "nicht bekannt" : promoStatus.reason === "expired" ? "abgelaufen" : promoStatus.reason === "max_uses_reached" ? "ausgeschöpft" : "ungültig"}`}
                  </div>
                )}
              </div>
            </div>
            <Button className="rounded-none bg-black text-white hover:bg-zinc-800" onClick={create} data-testid="submit-customer">Erstellen</Button>
          </DialogContent>
        </Dialog>
        </div>

      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="px-5 py-2">Nr.</th>
            <th className="px-5 py-2">Name</th>
            <th className="px-5 py-2">Typ</th>
            <th className="px-5 py-2">Kontakt</th>
            <th className="px-5 py-2">Adresse</th>
            <th className="px-5 py-2 w-24"></th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-400" data-testid="customers-empty">
              {customers.length === 0 ? "Noch keine Kunden" : `Keine Treffer${search ? ` für „${search}“` : ""}`}
            </td></tr>
          ) : filtered.map((c) => (
            <tr key={c.customer_id} className="border-t border-black/5 hover:bg-zinc-50" data-testid={`customer-row-${c.customer_id}`}>
              <td className="px-5 py-3 font-mono text-xs text-zinc-500">{c.customer_number || "—"}</td>
              <td className="px-5 py-3 font-medium">
                {(() => {
                  const name = c.type === "business"
                    ? (c.company_name || c.contact_person)
                    : [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
                  return name || c.email || c.phone || <span className="text-zinc-400">— (leer)</span>;
                })()}
                {c.contact_person && c.company_name && <div className="text-xs text-zinc-500">Ansprechpartner: {c.contact_person}</div>}
                {(c.complaints?.filter((x) => x.status !== "resolved").length > 0) && <span className="ml-2 inline-block text-[10px] uppercase tracking-widest font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">⚠ {c.complaints.filter((x) => x.status !== "resolved").length}</span>}
              </td>
              <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-wider font-bold">{c.type}</span></td>
              <td className="px-5 py-3 text-zinc-600 text-xs">{c.email}{c.phone && <div>{c.phone}</div>}</td>
              <td className="px-5 py-3 text-zinc-600 text-xs">{c.location?.street}, {c.location?.zip} {c.location?.city}</td>
              <td className="px-5 py-3 text-right">
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setDetail(c)} data-testid={`customer-detail-${c.customer_id}`}>Details</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {detail && <CustomerDetailModal customer={detail} tenant={tenant} onClose={() => setDetail(null)} onChange={() => { onChange(); }} />}
    </div>
  );
}

function CustomerDetailModal({ customer, tenant, onClose, onChange }) {
  const [c, setC] = useState(customer);
  const [busy, setBusy] = useState(false);
  const [complaintForm, setComplaintForm] = useState({ subject: "", description: "", pending_files: [] });
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(null); // {url, filename, mime_type} for inline PDF/image modal
  const [openSections, setOpenSections] = useState({
    complaints: false,
    pricing: false,
    properties: false,
    bins: false,
  });
  const allOpen = Object.values(openSections).every(Boolean);
  const toggleAll = () => {
    const next = !allOpen;
    setOpenSections({ complaints: next, pricing: next, properties: next, bins: next });
  };
  const toggle = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const refresh = async () => {
    const r = await api.get("/customers");
    const updated = r.data.find((x) => x.customer_id === customer.customer_id);
    if (updated) setC(updated);
    onChange();
  };

  const saveNotes = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.patch(`/customers/${c.customer_id}`, {
        internal_notes: c.internal_notes || "",
        driver_notes: c.driver_notes || "",
        contact_person: c.contact_person || "",
      });
      setMsg("Gespeichert");
      refresh();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const addComplaint = async () => {
    if (!complaintForm.subject) return;
    setBusy(true);
    try {
      const r = await api.post(`/customers/${c.customer_id}/complaints`, {
        subject: complaintForm.subject,
        description: complaintForm.description,
      });
      const newCid = r.data.complaint_id;
      // Anhänge (falls vorhanden) einzeln hochladen
      const files = complaintForm.pending_files || [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try { await api.post(`/customers/${c.customer_id}/complaints/${newCid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (uerr) { console.warn("attachment upload failed", f.name, uerr); }
      }
      setComplaintForm({ subject: "", description: "", pending_files: [] });
      refresh();
    } finally { setBusy(false); }
  };

  const uploadAttachmentToExisting = async (cid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/customers/${c.customer_id}/complaints/${cid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
    } catch (e) { alert("Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const removeAttachment = async (cid, aid) => {
    if (!window.confirm("Anhang entfernen?")) return;
    try {
      await api.delete(`/customers/${c.customer_id}/complaints/${cid}/attachments/${aid}`);
      refresh();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const changeComplaintStatus = async (cid, status) => {
    await api.patch(`/customers/${c.customer_id}/complaints/${cid}`, { status });
    refresh();
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-3">
            <span className="font-mono text-xs bg-zinc-100 px-2 py-1 rounded">{c.customer_number || "—"}</span>
            {c.type === "business" ? c.company_name : `${c.first_name} ${c.last_name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5" data-testid="customer-detail-modal">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-xs uppercase tracking-widest text-zinc-500">Kundennummer</span><div className="font-mono">{c.customer_number || "—"}</div></div>
            <div><span className="text-xs uppercase tracking-widest text-zinc-500">Typ</span><div>{c.type}</div></div>
            <div><span className="text-xs uppercase tracking-widest text-zinc-500">Email</span><div>{c.email}</div></div>
            <div><span className="text-xs uppercase tracking-widest text-zinc-500">Telefon</span><div>{c.phone || "—"}</div></div>
            <div className="sm:col-span-2"><span className="text-xs uppercase tracking-widest text-zinc-500">Adresse</span><div>{c.location?.street}, {c.location?.zip} {c.location?.city}</div></div>
          </div>

          {c.type === "business" && (
            <div>
              <Label className="text-xs">Kontaktperson</Label>
              <Input className="h-10 rounded-md" value={c.contact_person || ""} onChange={(e) => setC({ ...c, contact_person: e.target.value })} data-testid="detail-contact-person" />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Interne Bemerkung (nur Backoffice)</Label>
              <textarea rows="4" className="w-full text-sm p-2 border border-black/10 rounded-md" value={c.internal_notes || ""} onChange={(e) => setC({ ...c, internal_notes: e.target.value })} data-testid="detail-internal-notes" />
              <NoteAttachments customer={c} scope="internal" onChange={refresh} setPreview={setPreview} />
            </div>
            <div>
              <Label className="text-xs">Hinweis für Fahrer</Label>
              <textarea rows="4" className="w-full text-sm p-2 border border-black/10 rounded-md bg-yellow-50" value={c.driver_notes || ""} onChange={(e) => setC({ ...c, driver_notes: e.target.value })} data-testid="detail-driver-notes" />
              <p className="text-[10px] text-zinc-400 mt-1">Wird im Fahrer-View pro Stopp angezeigt</p>
              <NoteAttachments customer={c} scope="driver" onChange={refresh} setPreview={setPreview} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={saveNotes} disabled={busy} className="rounded-md text-white" style={{ background: "var(--tonneva-gradient)" }} data-testid="detail-save">Speichern</Button>
            <a
              href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/customers/${customer.customer_id}/gdpr-export.json`}
              target="_blank" rel="noreferrer"
              className="text-xs font-medium px-3 py-1.5 rounded-md border border-black/10 hover:bg-emerald-50 hover:border-emerald-200 text-zinc-700 inline-flex items-center gap-1.5"
              data-testid="detail-gdpr-export"
              title="DSGVO-Auskunftsersuchen (Art. 15) — alle Daten zu diesem Kunden als JSON"
            >
              📦 DSGVO-Export
            </a>
            {msg && <span className="text-emerald-700 text-sm" data-testid="detail-msg">{msg}</span>}
            {err && <span className="text-red-600 text-sm">{err}</span>}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-black/10">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Weitere Details</div>
            <button onClick={toggleAll} type="button" className="text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-zinc-50 inline-flex items-center gap-1.5" data-testid="detail-toggle-all">
              {allOpen ? "▲ Alle schließen" : "▼ Alle öffnen"}
            </button>
          </div>

          <CollapsibleSection
            open={openSections.complaints}
            onToggle={() => toggle("complaints")}
            title={`Beschwerden (${c.complaints?.length || 0})`}
            testid="section-complaints"
          >
            <div className="grid sm:grid-cols-3 gap-2 mb-3">
              <Input className="h-9 rounded-md sm:col-span-1" placeholder="Betreff" value={complaintForm.subject} onChange={(e) => setComplaintForm({ ...complaintForm, subject: e.target.value })} data-testid="complaint-subject" />
              <Input className="h-9 rounded-md sm:col-span-2" placeholder="Beschreibung" value={complaintForm.description} onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })} data-testid="complaint-description" />
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-dashed border-black/20 rounded-md cursor-pointer hover:bg-zinc-50" data-testid="complaint-attach-label">
                📎 Anhang wählen
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.docx,.xlsx,.txt,.csv"
                  className="hidden"
                  onChange={(e) => setComplaintForm({ ...complaintForm, pending_files: [...(complaintForm.pending_files || []), ...Array.from(e.target.files || [])] })}
                  data-testid="complaint-attach-input"
                />
              </label>
              {(complaintForm.pending_files || []).map((f, i) => (
                <span key={`${f.name}-${f.size}-${f.lastModified}`} className="text-[10px] px-2 py-1 bg-blue-50 text-blue-700 rounded-md inline-flex items-center gap-1" data-testid={`complaint-pending-file-${i}`}>
                  {f.name}
                  <button
                    type="button"
                    onClick={() => setComplaintForm({ ...complaintForm, pending_files: complaintForm.pending_files.filter((_, j) => j !== i) })}
                    className="ml-1 hover:text-red-600"
                  >×</button>
                </span>
              ))}
            </div>
            <Button size="sm" onClick={addComplaint} disabled={busy || !complaintForm.subject} className="rounded-md text-xs" data-testid="complaint-add">+ Beschwerde erfassen</Button>
            <div className="mt-3 space-y-2 max-h-52 overflow-y-auto">
              {(c.complaints || []).map((cp) => (
                <div key={cp.complaint_id} className="p-2.5 border border-black/5 rounded-md space-y-2" data-testid={`complaint-${cp.complaint_id}`}>
                  <div className="flex items-center justify-between text-xs">
                    <div>
                      <div className="font-semibold">{cp.subject}</div>
                      {cp.description && <div className="text-zinc-600">{cp.description}</div>}
                      <div className="text-zinc-400 text-[10px] mt-0.5">{new Date(cp.created_at).toLocaleString("de-DE")}</div>
                    </div>
                    <select value={cp.status} onChange={(e) => changeComplaintStatus(cp.complaint_id, e.target.value)} className="text-xs border border-black/10 rounded-md px-2 py-1" data-testid={`complaint-status-${cp.complaint_id}`}>
                      <option value="open">Offen</option>
                      <option value="in_progress">In Bearbeitung</option>
                      <option value="resolved">Gelöst</option>
                    </select>
                  </div>
                  {(cp.attachments && cp.attachments.length > 0) && (
                    <div className="flex flex-wrap gap-1.5">
                      {cp.attachments.map((a) => {
                        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                        return (
                          <AttachmentThumb
                            key={a.attachment_id}
                            attachment={a}
                            backend={backend}
                            onClick={() => setPreview({ url: `${backend}${a.url}`, filename: a.filename, mime_type: a.mime_type })}
                            onRemove={() => removeAttachment(cp.complaint_id, a.attachment_id)}
                            onSaveNote={async (note) => {
                              await api.patch(`/customers/${c.customer_id}/complaints/${cp.complaint_id}/attachments/${a.attachment_id}`, { note });
                              refresh?.();
                            }}
                            testid={`attachment-${a.attachment_id}`}
                          />
                        );
                      })}
                    </div>
                  )}
                  <label className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-800 cursor-pointer" data-testid={`add-attachment-${cp.complaint_id}`}>
                    📎 Anhang hinzufügen
                    <input
                      type="file"
                      accept="image/*,.pdf,.docx,.xlsx,.txt,.csv"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachmentToExisting(cp.complaint_id, f); e.target.value = ""; }}
                    />
                  </label>
                </div>
              ))}
              {(c.complaints || []).length === 0 && <div className="text-xs text-zinc-400 py-3 text-center">Keine Beschwerden</div>}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            open={openSections.pricing}
            onToggle={() => toggle("pricing")}
            title="Preise, Rabatt & HUB"
            testid="section-pricing"
          >
            <CustomerPricingSubPanel
              customer={c}
              tenant={tenant}
              onSaved={(updated) => { setC(updated); onChange(); }}
            />
          </CollapsibleSection>

          <CollapsibleSection
            open={openSections.properties}
            onToggle={() => toggle("properties")}
            title={`Objekte / Adressen (${c.properties?.length || 0})`}
            testid="section-properties"
          >
            <PropertiesSubPanel customer={c} onChange={refresh} />
          </CollapsibleSection>

          <CollapsibleSection
            open={openSections.bins}
            onToggle={() => toggle("bins")}
            title={`Tonnen (${c.bins?.length || 0})`}
            testid="section-bins"
          >
            <BinsSubPanel customer={c} onChange={refresh} />
          </CollapsibleSection>
        </div>
        {preview && <AttachmentPreviewOverlay preview={preview} onClose={() => setPreview(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function AttachmentThumb({ attachment, backend, onClick, onRemove, onSaveNote, testid }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(attachment.note || "");
  useEffect(() => { setNote(attachment.note || ""); }, [attachment.note]);
  const isImage = (attachment.mime_type || "").startsWith("image/");
  const tooltip = attachment.note || attachment.filename;
  const save = async () => {
    try {
      await onSaveNote?.((note || "").trim().slice(0, 200));
      setEditing(false);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  return (
    <div className={`flex items-start gap-1 text-[10px] bg-zinc-50 border rounded px-1.5 py-1 relative group ${attachment.note ? "border-amber-300" : "border-black/10"}`} data-testid={testid} title={tooltip}>
      <div className="relative">
        {isImage ? (
          <button type="button" onClick={onClick}>
            <img src={`${backend}${attachment.url}`} alt={attachment.filename} className="w-10 h-10 object-cover rounded" />
          </button>
        ) : (
          <button type="button" onClick={onClick} className="text-blue-600 hover:underline">
            📄 {(attachment.filename || "").length > 20 ? attachment.filename.slice(0, 17) + "…" : attachment.filename}
          </button>
        )}
        {attachment.note && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full border border-white text-[8px] text-white flex items-center justify-center" title={attachment.note}>💬</span>
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        {editing ? (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              autoFocus
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setNote(attachment.note || ""); } }}
              placeholder="Kurze Notiz (max. 200 Zeichen)…"
              className="text-[10px] px-1.5 py-0.5 border border-black/20 rounded w-52"
              data-testid={`${testid}-note-input`}
            />
            <button type="button" onClick={save} className="text-emerald-600 hover:text-emerald-800" title="Speichern" data-testid={`${testid}-note-save`}>✓</button>
            <button type="button" onClick={() => { setEditing(false); setNote(attachment.note || ""); }} className="text-zinc-400 hover:text-zinc-700">✕</button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setEditing(true)} className="text-zinc-500 hover:text-zinc-800" title={attachment.note ? "Notiz bearbeiten" : "Notiz hinzufügen"} data-testid={`${testid}-note-btn`}>
              {attachment.note ? "✎" : "✎"}
            </button>
            <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700" title="Entfernen">×</button>
          </div>
        )}
        {attachment.note && !editing && (
          <div className="text-[9px] text-amber-700 italic max-w-[160px] truncate" title={attachment.note}>„{attachment.note}"</div>
        )}
      </div>
    </div>
  );
}


function NoteAttachments({ customer, scope, onChange, setPreview }) {
  const field = scope === "internal" ? "internal_note_attachments" : "driver_note_attachments";
  const list = customer?.[field] || [];
  const [uploading, setUploading] = useState(false);
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/customers/${customer.customer_id}/note-attachments/${scope}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.();
    } catch (e) { alert("Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };
  const remove = async (aid) => {
    if (!window.confirm("Anhang entfernen?")) return;
    try {
      await api.delete(`/customers/${customer.customer_id}/note-attachments/${scope}/${aid}`);
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const saveNote = async (aid, note) => {
    await api.patch(`/customers/${customer.customer_id}/note-attachments/${scope}/${aid}`, { note });
    onChange?.();
  };
  return (
    <div className="mt-2 space-y-1" data-testid={`note-attachments-${scope}`}>
      <div className="flex flex-wrap gap-1.5">
        {list.map((a) => (
          <AttachmentThumb
            key={a.attachment_id}
            attachment={a}
            backend={backend}
            onClick={() => setPreview({ url: `${backend}${a.url}`, filename: a.filename, mime_type: a.mime_type })}
            onRemove={() => remove(a.attachment_id)}
            onSaveNote={(note) => saveNote(a.attachment_id, note)}
            testid={`note-att-${a.attachment_id}`}
          />
        ))}
      </div>
      <label className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-800 cursor-pointer" data-testid={`add-note-att-${scope}`}>
        📎 {uploading ? "Lädt…" : "Anhang hinzufügen"}
        <input
          type="file"
          accept="image/*,.pdf,.docx,.xlsx,.txt,.csv"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
        />
      </label>
    </div>
  );
}

function AttachmentPreviewOverlay({ preview, onClose }) {
  const isPdf = (preview.mime_type || "").includes("pdf") || preview.url.toLowerCase().endsWith(".pdf");
  const isImage = (preview.mime_type || "").startsWith("image/");
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="attachment-preview-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/10 bg-zinc-50">
          <div className="text-sm font-semibold truncate">{preview.filename}</div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a href={preview.url} download={preview.filename} className="text-[11px] px-2 py-1 rounded hover:bg-zinc-200 text-zinc-700" data-testid="preview-download">
              ⬇ Herunterladen
            </a>
            <button onClick={onClose} className="text-[11px] px-2 py-1 rounded hover:bg-zinc-200" data-testid="preview-close">✕ Schließen</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-zinc-100">
          {isImage && <img src={preview.url} alt={preview.filename} className="w-full h-auto max-h-[85vh] object-contain mx-auto" />}
          {isPdf && <iframe src={preview.url} title={preview.filename} className="w-full h-[85vh] border-0" data-testid="preview-pdf-frame" />}
          {!isImage && !isPdf && (
            <div className="p-8 text-center text-sm text-zinc-600">
              Vorschau nicht verfügbar. <a href={preview.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">In neuem Tab öffnen</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CollapsibleSection({ open, onToggle, title, testid, children }) {
  return (
    <div className="border border-black/10 rounded-lg overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-zinc-50 hover:bg-zinc-100 transition"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <span className="font-semibold text-sm">{title}</span>
        <span className="text-zinc-500 text-xs font-mono">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="p-4 border-t border-black/10">{children}</div>}
    </div>
  );
}

function SubsPanel({ subs, customers, onChange, tenant }) {
  const findCust = (id) => customers.find((c) => c.customer_id === id);
  const eur = (cents) => `€${(cents / 100).toFixed(2)}`;
  const [pausingId, setPausingId] = useState(null);
  const [pauseUntil, setPauseUntil] = useState("");
  const [cancelingId, setCancelingId] = useState(null);
  const tenantNotice = Number(tenant?.notice_period_months) || 3;
  const [cancelForm, setCancelForm] = useState({ months: tenantNotice, immediate: false, reason: "" });

  const doPause = async (subId) => {
    if (!pauseUntil) { alert("Bitte Datum wählen"); return; }
    try {
      await api.post(`/subscriptions/${subId}/pause`, { paused_until: pauseUntil });
      setPausingId(null); setPauseUntil("");
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const doResume = async (subId) => {
    try { await api.post(`/subscriptions/${subId}/resume`); onChange?.(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const doCancel = async (subId) => {
    const s = subs.find(x => x.subscription_id === subId);
    const suggested = Number(s?.notice_period_months) || tenantNotice;
    setCancelingId(subId);
    setCancelForm({ months: suggested, immediate: false, reason: "" });
  };
  const submitCancel = async () => {
    if (!cancelingId) return;
    try {
      const payload = {
        cancel_reason: cancelForm.reason || "backoffice",
        immediate: !!cancelForm.immediate,
      };
      if (!cancelForm.immediate) payload.notice_period_months = Number(cancelForm.months) || tenantNotice;
      await api.post(`/subscriptions/${cancelingId}/cancel`, payload);
      setCancelingId(null);
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const doReactivate = async (subId) => {
    if (!confirm("Kündigung wirklich zurückziehen? Alle Termine bis zum ursprünglichen Ablaufdatum werden reaktiviert.")) return;
    try { await api.post(`/subscriptions/${subId}/reactivate`); onChange?.(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const previewEndDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + Number(cancelForm.months || 0));
    return d.toLocaleDateString("de-DE");
  };

  const statusColor = (s) => s === "active" ? "text-emerald-600" : s === "paused" ? "text-amber-600" : s === "pending_cancellation" ? "text-orange-600" : "text-red-600";
  const statusLabel = (s) => s === "pending_cancellation" ? "läuft aus" : s;

  return (
    <div className="bg-white border border-black/10">
      <div className="px-5 py-3 border-b border-black/10"><h3 className="font-heading text-lg font-semibold tracking-tight">Abos & Verträge ({subs.length})</h3></div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-zinc-500">
          <tr><th className="px-5 py-2">Kunde</th><th className="px-5 py-2">Plan</th><th className="px-5 py-2">Frequenz</th><th className="px-5 py-2">Preis</th><th className="px-5 py-2">Nächster Termin</th><th className="px-5 py-2">Status</th><th className="px-5 py-2 w-56">Aktionen</th></tr>
        </thead>
        <tbody>
          {subs.length === 0 ? (
            <tr><td colSpan={7} className="px-5 py-8 text-center text-zinc-400">Noch keine Abos</td></tr>
          ) : subs.map((s) => {
            const c = findCust(s.customer_id);
            return (
              <tr key={s.subscription_id} className="border-t border-black/5" data-testid={`sub-row-${s.subscription_id}`}>
                <td className="px-5 py-3 font-medium">{c ? (c.type === "business" ? c.company_name : `${c.first_name} ${c.last_name}`) : s.customer_id}</td>
                <td className="px-5 py-3">{s.plan_type}</td>
                <td className="px-5 py-3">{s.frequency}</td>
                <td className="px-5 py-3 font-mono">{eur(s.price_per_service_cents)}</td>
                <td className="px-5 py-3 text-zinc-600">{s.next_service_date ? new Date(s.next_service_date).toLocaleDateString("de-DE") : "—"}{s.paused_until && <div className="text-[10px] text-amber-600">pausiert bis {new Date(s.paused_until).toLocaleDateString("de-DE")}</div>}{s.status === "pending_cancellation" && s.ends_at && <div className="text-[10px] text-orange-600 font-semibold">endet am {new Date(s.ends_at).toLocaleDateString("de-DE")}</div>}</td>
                <td className="px-5 py-3"><span className={`text-[10px] uppercase tracking-wider font-bold ${statusColor(s.status)}`}>{statusLabel(s.status)}</span></td>
                <td className="px-5 py-3">
                  <div className="flex flex-col gap-1">
                    {s.status === "canceled" ? (
                      <span className="text-[10px] text-zinc-400 italic">Gekündigt {s.canceled_at ? "am " + new Date(s.canceled_at).toLocaleDateString("de-DE") : ""}</span>
                    ) : s.status === "pending_cancellation" ? (
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-emerald-700 hover:bg-emerald-50" onClick={() => doReactivate(s.subscription_id)} data-testid={`reactivate-btn-${s.subscription_id}`}>Kündigung widerrufen</Button>
                      </div>
                    ) : pausingId === s.subscription_id ? (
                      <div className="flex gap-1">
                        <input type="date" className="text-xs px-2 py-1 border border-black/10 rounded" value={pauseUntil} onChange={(e) => setPauseUntil(e.target.value)} data-testid={`pause-date-${s.subscription_id}`} />
                        <Button size="sm" className="rounded-none h-7 text-[10px] bg-amber-600 text-white" onClick={() => doPause(s.subscription_id)} data-testid={`pause-confirm-${s.subscription_id}`}>OK</Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={() => { setPausingId(null); setPauseUntil(""); }}>✕</Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {s.status === "active" ? (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => { setPausingId(s.subscription_id); setPauseUntil(new Date(Date.now()+14*86400000).toISOString().slice(0,10)); }} data-testid={`pause-btn-${s.subscription_id}`}>Pausieren</Button>
                        ) : s.status === "paused" ? (
                          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-emerald-600" onClick={() => doResume(s.subscription_id)} data-testid={`resume-btn-${s.subscription_id}`}>Fortsetzen</Button>
                        ) : null}
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-red-600 hover:bg-red-50" onClick={() => doCancel(s.subscription_id)} data-testid={`cancel-btn-${s.subscription_id}`}>Kündigen</Button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {cancelingId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCancelingId(null)} data-testid="cancel-dialog">
          <div className="bg-white p-6 max-w-md w-full border border-black/10" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-heading font-semibold text-lg mb-2">Abo kündigen</h4>
            <p className="text-xs text-zinc-600 mb-4">Reinigungen laufen bis zum Ablaufdatum weiter. Danach wird das Abo automatisch beendet.</p>
            <label className="flex items-center gap-2 mb-3 text-sm">
              <input type="checkbox" checked={cancelForm.immediate} onChange={(e) => setCancelForm({ ...cancelForm, immediate: e.target.checked })} data-testid="cancel-immediate" />
              <span>Sofort kündigen (alle offenen Termine absagen)</span>
            </label>
            {!cancelForm.immediate && (
              <div className="mb-3">
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Kündigungsfrist (Monate)</label>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" max="24" value={cancelForm.months} onChange={(e) => setCancelForm({ ...cancelForm, months: e.target.value })} className="w-24 px-3 py-2 border border-black/10 rounded text-sm" data-testid="cancel-months" />
                  <span className="text-xs text-zinc-500">Standard: {tenantNotice} Monate · Endet am <b>{previewEndDate()}</b></span>
                </div>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Grund (optional)</label>
              <input type="text" value={cancelForm.reason} onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })} placeholder="z. B. Kunde umgezogen" className="w-full px-3 py-2 border border-black/10 rounded text-sm" data-testid="cancel-reason" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setCancelingId(null)} data-testid="cancel-dialog-abort">Abbrechen</Button>
              <Button className="bg-red-600 text-white hover:bg-red-700" onClick={submitCancel} data-testid="cancel-dialog-confirm">
                {cancelForm.immediate ? "Sofort kündigen" : "Zum Vertragsende kündigen"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoutesPanel({ jobs, customers, onChange }) {
  const setStatus = async (jobId, status) => {
    try { await api.patch(`/jobs/${jobId}`, { status }); onChange(); } catch (e) { alert(e.message); }
  };
  const stopsToday = jobs
    .filter((j) => customers.find((c) => c.customer_id === j.customer_id)?.location?.lng)
    .map((j) => {
      const c = customers.find((x) => x.customer_id === j.customer_id);
      return { lng: c.location.lng, lat: c.location.lat, label: `${c.first_name} ${c.last_name}` };
    });
  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-3 bg-white border border-black/10 p-5">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-3">Tour-Karte</h3>
        <MapView stops={stopsToday} height="420px" />
      </div>
      <div className="md:col-span-2 bg-white border border-black/10">
        <div className="px-5 py-3 border-b border-black/10"><h3 className="font-heading text-lg font-semibold tracking-tight">Heutige Jobs</h3></div>
        <div className="divide-y divide-black/5 max-h-[420px] overflow-y-auto">
          {jobs.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-400">Keine geplanten Jobs</div>
          ) : jobs.map((j) => {
            const c = customers.find((x) => x.customer_id === j.customer_id);
            return (
              <div key={j.job_id} className="p-4 flex items-center justify-between" data-testid={`job-row-${j.job_id}`}>
                <div>
                  <div className="text-sm font-medium">{c ? `${c.first_name} ${c.last_name}` : j.customer_id}</div>
                  <div className="text-xs text-zinc-500">{c?.location?.street}, {c?.location?.city}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-1" style={{ color: j.status === "done" ? "#16A34A" : j.status === "in_progress" ? "#F59E0B" : "#6B7280" }}>{j.status}</div>
                </div>
                {j.status !== "done" && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs h-7" onClick={() => setStatus(j.job_id, "done")} data-testid={`complete-job-${j.job_id}`}>
                    Erledigt
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BillingPanel({ customers, onChange, tenant, onTenantChange }) {
  const [invoices, setInvoices] = useState([]);
  const [monthly, setMonthly] = useState({ year: new Date().getFullYear(), month: Math.max(1, new Date().getMonth()), preview: null, running: false });
  const [bank, setBank] = useState({ bank_iban: "", bank_bic: "", bank_holder: "", sepa_creditor_id: "" });
  const [bankMsg, setBankMsg] = useState(null);
  // Nummernkreise
  const [numBusy, setNumBusy] = useState(false);
  const [numMsg, setNumMsg] = useState(null);
  const [custStart, setCustStart] = useState(tenant?.customer_number_start ?? "");
  const [custEnd, setCustEnd] = useState(tenant?.customer_number_end ?? "");
  const [invFmt, setInvFmt] = useState(tenant?.invoice_number_format || "RE-{YEAR}-{SEQ:05d}");
  const [invSeqStart, setInvSeqStart] = useState(tenant?.invoice_number_seq_start ?? 1);
  useEffect(() => {
    setCustStart(tenant?.customer_number_start ?? "");
    setCustEnd(tenant?.customer_number_end ?? "");
    setInvFmt(tenant?.invoice_number_format || "RE-{YEAR}-{SEQ:05d}");
    setInvSeqStart(tenant?.invoice_number_seq_start ?? 1);
  }, [tenant?.customer_number_start, tenant?.customer_number_end, tenant?.invoice_number_format, tenant?.invoice_number_seq_start]);
  const saveNumbering = async (patch) => {
    setNumBusy(true);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}`, patch);
      onTenantChange?.(r.data);
      setNumMsg({ ok: true, msg: "Gespeichert" });
      setTimeout(() => setNumMsg(null), 2500);
    } catch (e) {
      setNumMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setNumBusy(false); }
  };
  const invPreview = (() => {
    try {
      const y = new Date().getFullYear();
      const m = new Date().getMonth() + 1;
      return String(invFmt || "")
        .replace(/\{YEAR\}/g, String(y))
        .replace(/\{MONTH\}/g, String(m))
        .replace(/\{TENANT\}/g, ((tenant?.slug || "TON").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3)) || "TON")
        .replace(/\{SEQ(?::0?(\d+)d)?\}/g, (_m, pad) => String(Number(invSeqStart) || 1).padStart(Number(pad || 0), "0"));
    } catch { return "—"; }
  })();
  useEffect(() => {
    setBank({
      bank_iban: tenant?.bank_iban || "",
      bank_bic: tenant?.bank_bic || "",
      bank_holder: tenant?.bank_holder || tenant?.name || "",
      sepa_creditor_id: tenant?.sepa_creditor_id || "",
    });
  }, [tenant]);
  const saveBank = async () => {
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/bank-details`, bank);
      onTenantChange?.(r.data);
      setBankMsg({ ok: true, msg: "Gespeichert" });
      setTimeout(() => setBankMsg(null), 2500);
    } catch (e) {
      setBankMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    }
  };
  const loadInvoices = async () => { const r = await api.get("/invoices"); setInvoices(r.data); };
  useEffect(() => { loadInvoices(); }, []);
  const createInv = async (customerId) => {
    try {
      await api.post("/invoices", { customer_id: customerId, items: [{ description: "Tonnenreinigung", amount_cents: 1490 }] });
      await loadInvoices();
    } catch (e) { alert(e.message); }
  };
  const runMonthly = async (dryRun) => {
    setMonthly({ ...monthly, running: true });
    try {
      const r = await api.post("/invoices/generate-monthly", { year: +monthly.year, month: +monthly.month, dry_run: dryRun });
      setMonthly({ ...monthly, running: false, preview: r.data });
      if (!dryRun) await loadInvoices();
    } catch (e) {
      setMonthly({ ...monthly, running: false, preview: { error: e.response?.data?.detail || "Fehler" } });
    }
  };
  const eur = (c) => `€${(c / 100).toFixed(2)}`;
  return (
    <div className="space-y-6">
      {/* Nummernkreise (Kundennummer + Rechnungsnummer) */}
      <section className="bg-white border border-black/10 p-5" data-testid="numbering-panel">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Nummernkreise: Kunden & Rechnungen</h3>
          {numMsg && <span className={`text-xs font-medium ${numMsg.ok ? "text-emerald-700" : "text-red-600"}`}>{numMsg.ok ? "✓" : "✗"} {numMsg.msg}</span>}
        </div>
        <p className="text-xs text-zinc-500 mb-4">Fortlaufende Nummerierung pro Firma — vermeidet DATEV-Kollisionen bei mehreren Tenants. Neue Kundennummern werden automatisch aus dem Bereich vergeben; bestehende Nummern bleiben unverändert.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-black/10 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-3">Kundennummer</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Von</Label>
                <Input type="number" min={1} value={custStart} onChange={(e) => setCustStart(e.target.value)} placeholder="z. B. 20000" data-testid="tenant-cust-start" />
              </div>
              <div>
                <Label className="text-xs">Bis</Label>
                <Input type="number" min={1} value={custEnd} onChange={(e) => setCustEnd(e.target.value)} placeholder="z. B. 69999" data-testid="tenant-cust-end" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" variant="outline" disabled={numBusy} onClick={() => saveNumbering({
                customer_number_start: custStart === "" ? null : Number(custStart),
                customer_number_end: custEnd === "" ? null : Number(custEnd),
              })} data-testid="save-tenant-cust-range">Speichern</Button>
              <span className="text-[10px] text-zinc-500">
                {custStart !== "" ? <>Nächste freie Nr. wird zwischen <b>{custStart}</b>–<b>{custEnd || "∞"}</b> vergeben</> : <span className="italic">Leer = Slug-Prefix-Format (Legacy)</span>}
              </span>
            </div>
            <p className="text-[10px] text-zinc-400 mt-2">Beispiel GTR: 20000–69999. Kollidiert nicht mit anderer Firma im DATEV-Export.</p>
          </div>
          <div className="border border-black/10 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-3">Rechnungsnummer</div>
            <div>
              <Label className="text-xs">Format</Label>
              <Input value={invFmt} onChange={(e) => setInvFmt(e.target.value)} placeholder="RE-{YEAR}-{SEQ:05d}" data-testid="tenant-inv-format" />
              <p className="text-[10px] text-zinc-500 mt-1">Platzhalter: <code className="bg-zinc-100 px-1 rounded">{"{YEAR}"}</code> · <code className="bg-zinc-100 px-1 rounded">{"{MONTH}"}</code> · <code className="bg-zinc-100 px-1 rounded">{"{TENANT}"}</code> · <code className="bg-zinc-100 px-1 rounded">{"{SEQ:05d}"}</code> (5-stellig aufgefüllt)</p>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Start-Sequenz</Label>
              <Input type="number" min={1} value={invSeqStart} onChange={(e) => setInvSeqStart(e.target.value)} className="w-40" data-testid="tenant-inv-seq-start" />
            </div>
            <div className="mt-3 p-2 bg-emerald-50 border border-emerald-100 rounded text-xs" data-testid="invoice-preview">
              <span className="text-zinc-500">Vorschau:</span>{" "}
              <span className="font-mono font-semibold">{invPreview}</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" variant="outline" disabled={numBusy} onClick={() => saveNumbering({
                invoice_number_format: invFmt || null,
                invoice_number_seq_start: Number(invSeqStart) || 1,
              })} data-testid="save-tenant-inv-format">Speichern</Button>
              <span className="text-[10px] text-zinc-500">Wirkt nur auf neu erstellte Rechnungen</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bank-Details für Rechnungsstellung */}
      <section className="bg-white border border-black/10 p-5" data-testid="bank-details-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-2 flex items-center gap-2"><Receipt size={18} weight="bold" /> Bankverbindung für Rechnungen</h3>
        <p className="text-xs text-zinc-500 mb-4">Diese Bankverbindung erscheint auf allen Rechnungen bei Zahlungsart „Rechnung“. SEPA-Kunden sehen zusätzlich Ihre Gläubiger-ID.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Kontoinhaber</Label><Input className="h-10" value={bank.bank_holder} onChange={(e) => setBank({ ...bank, bank_holder: e.target.value })} placeholder="Firmenname" data-testid="bank-holder" /></div>
          <div><Label className="text-xs">IBAN</Label><Input className="h-10 font-mono uppercase" value={bank.bank_iban} onChange={(e) => setBank({ ...bank, bank_iban: e.target.value.toUpperCase() })} placeholder="DE89 3704 0044 0532 0130 00" data-testid="bank-iban" /></div>
          <div><Label className="text-xs">BIC</Label><Input className="h-10 font-mono uppercase" value={bank.bank_bic} onChange={(e) => setBank({ ...bank, bank_bic: e.target.value.toUpperCase() })} placeholder="COBADEFFXXX" data-testid="bank-bic" /></div>
          <div><Label className="text-xs">SEPA Gläubiger-ID</Label><Input className="h-10 font-mono uppercase" value={bank.sepa_creditor_id} onChange={(e) => setBank({ ...bank, sepa_creditor_id: e.target.value.toUpperCase() })} placeholder="DE98ZZZ09999999999" data-testid="bank-creditor" /></div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <Button onClick={saveBank} className="rounded-none bg-black text-white hover:bg-zinc-800 h-10" data-testid="save-bank">Bankdaten speichern</Button>
          {bankMsg && <span className={`text-xs font-medium ${bankMsg.ok ? "text-emerald-700" : "text-red-600"}`}>{bankMsg.ok ? "✓" : "✗"} {bankMsg.msg}</span>}
        </div>
      </section>

      {/* Automatische Monatsabrechnung */}
      <section className="bg-gradient-to-br from-indigo-50 to-white border border-black/10 p-5" data-testid="monthly-invoicing">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-2 flex items-center gap-2"><Receipt size={18} weight="bold" /> Automatische Monatsabrechnung</h3>
        <p className="text-xs text-zinc-500 mb-4">Läuft automatisch am <strong>1. Werktag jedes Monats um 06:00 Uhr</strong> und erstellt Sammel-Rechnungen für alle Kunden mit erledigten Jobs — inklusive automatischer Berücksichtigung der Promo-Rabatte (respektiert <em>Erst-Buchung</em> vs. <em>jede Buchung</em>).</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">Jahr</Label>
            <Input type="number" min="2024" max="2030" className="h-10 w-24 rounded-none" value={monthly.year} onChange={(e) => setMonthly({ ...monthly, year: e.target.value })} data-testid="monthly-year" />
          </div>
          <div>
            <Label className="text-xs">Monat</Label>
            <Input type="number" min="1" max="12" className="h-10 w-20 rounded-none" value={monthly.month} onChange={(e) => setMonthly({ ...monthly, month: e.target.value })} data-testid="monthly-month" />
          </div>
          <Button onClick={() => runMonthly(true)} disabled={monthly.running} variant="outline" className="h-10 rounded-none" data-testid="monthly-preview">
            Vorschau
          </Button>
          <Button onClick={() => { if (window.confirm(`Rechnungen für ${monthly.month}/${monthly.year} erstellen?`)) runMonthly(false); }} disabled={monthly.running} className="h-10 bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="monthly-generate">
            {monthly.running ? "Läuft…" : "Jetzt erstellen"}
          </Button>
        </div>
        {monthly.preview && (
          <div className="mt-4 text-xs bg-white border border-black/10 p-3" data-testid="monthly-result">
            {monthly.preview.error ? <span className="text-red-600">Fehler: {monthly.preview.error}</span> : (
              <div className="space-y-2">
                <div>Periode <strong>{monthly.preview.period}</strong> · Erstellt: <strong>{monthly.preview.invoices_created}</strong> · Übersprungen (bereits vorhanden): {monthly.preview.invoices_skipped_existing || 0} {monthly.preview.dry_run && <span className="text-amber-600 ml-2 font-bold">DRY-RUN — nichts gespeichert</span>}</div>
                {(monthly.preview.details || []).map((d, i) => (
                  <div key={d.customer_id || d.invoice_number || i} className="flex items-center gap-3 border-t border-black/5 pt-1 font-mono text-[11px]">
                    <span className="w-40 truncate">{d.customer_name}</span>
                    <span className="w-16">{d.invoice_number}</span>
                    <span>{d.jobs_count} Jobs</span>
                    <span>€{(d.subtotal_cents / 100).toFixed(2)}</span>
                    {d.discount_cents > 0 && <span className="text-emerald-700">− €{(d.discount_cents / 100).toFixed(2)} ({d.discount_label})</span>}
                    <span className="ml-auto font-bold">€{(d.total_cents / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10">
          <div className="px-5 py-3 border-b border-black/10"><h3 className="font-heading text-lg font-semibold tracking-tight">Rechnungen ({invoices.length})</h3></div>
          <div className="divide-y divide-black/5 max-h-96 overflow-y-auto">
            {invoices.length === 0 ? <div className="p-6 text-center text-zinc-400 text-sm">Noch keine Rechnungen</div> : invoices.map((i) => (
              <div key={i.invoice_id} className="p-4 flex items-center justify-between text-sm gap-3" data-testid={`invoice-row-${i.invoice_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono">{i.number}</div>
                  <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-1">
                    {i.period && <span>{i.period}</span>}
                    <span>· {new Date(i.created_at).toLocaleDateString("de-DE")}</span>
                    {i.auto_generated && <span className="text-[10px] uppercase tracking-widest text-indigo-600 font-bold ml-1">Auto</span>}
                    {i.email_sent === true && <span className="text-[10px] text-emerald-700 font-bold ml-1" title={`gesendet am ${(i.email_sent_at || '').slice(0,16)}`}>✓ Email</span>}
                    {i.email_sent === false && <span className="text-[10px] text-red-600 font-bold ml-1" title={i.email_status || ''}>⚠ Email</span>}
                  </div>
                  {i.discount_cents > 0 && (
                    <div className="text-[11px] text-emerald-700 mt-0.5">−{eur(i.discount_cents)} {i.discount_label}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-mono font-medium">{eur(i.total_cents)}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{i.status}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${i.invoice_id}/pdf`} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 border border-black/10 hover:bg-zinc-50 text-center" data-testid={`invoice-pdf-${i.invoice_id}`} title="PDF öffnen">PDF</a>
                  <button onClick={async () => {
                    const r = await api.post(`/invoices/${i.invoice_id}/resend`);
                    alert(r.data.ok ? "Email verschickt." : `Fehler: ${r.data.message}`);
                    await loadInvoices();
                  }} className="text-[10px] px-2 py-1 border border-black/10 hover:bg-zinc-50" data-testid={`invoice-resend-${i.invoice_id}`} title="Erneut per E-Mail versenden">↻ Email</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-black/10">
          <div className="px-5 py-3 border-b border-black/10"><h3 className="font-heading text-lg font-semibold tracking-tight">Schnellrechnung erstellen</h3></div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {customers.map((c) => (
              <div key={c.customer_id} className="flex items-center justify-between border border-black/10 p-3">
                <div className="text-sm">{(() => {
                  const name = c.type === "business"
                    ? (c.company_name || c.contact_person)
                    : [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
                  return name || c.email || c.phone || `Kunde ${c.customer_id?.slice(-4)}`;
                })()}</div>
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => createInv(c.customer_id)} data-testid={`create-invoice-${c.customer_id}`}>
                  <Receipt size={12} weight="bold" className="mr-1" /> Rechnung
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {tenant && (
        <AccountingPanel
          tenant={tenant}
          invoices={invoices}
          onConfigSaved={(t) => { onTenantChange?.(t); loadInvoices(); }}
        />
      )}
    </div>
  );
}

function BrandingPanel({ tenant, onSaved }) {
  const [b, setB] = useState(tenant?.branding || {});
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);
  const fileRef = React.useRef(null);
  const POPULAR_FONTS = [
    "Outfit", "Inter", "Roboto", "Montserrat", "Open Sans", "Lato", "Poppins",
    "Raleway", "Nunito", "Playfair Display", "DM Sans", "Work Sans",
    "IBM Plex Sans", "Source Sans 3", "Manrope", "Plus Jakarta Sans", "Fira Sans",
  ];

  useEffect(() => { setB(tenant?.branding || {}); }, [tenant]);

  // Inject Google Fonts dynamically when font_family changes
  useEffect(() => {
    const fam = b.font_family;
    if (!fam) return;
    const id = "tonneva-brand-font";
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement("link");
      link.id = id; link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam)}:wght@400;500;600;700;800&display=swap`;
  }, [b.font_family]);

  if (!tenant) return <div className="p-6 text-sm text-zinc-500">Lade…</div>;

  const save = async () => {
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}`, { branding: b });
      onSaved(r.data); alert("Branding gespeichert!");
    } catch (e) { alert(e.message); }
  };

  const onLogoFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploadErr(null); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/tenants/${tenant.tenant_id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newB = { ...b, logo_url: r.data.url };
      setB(newB);
      // Persist branding incl. fresh logo immediately so a refresh keeps it
      const t = await api.patch(`/tenants/${tenant.tenant_id}`, { branding: newB });
      onSaved(t.data);
    } catch (e) {
      setUploadErr(e.response?.data?.detail || e.message);
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const fullLogoUrl = b.logo_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}` : b.logo_url;

  return (
    <div className="bg-white border border-black/10 p-6 max-w-2xl">
      <h3 className="font-heading text-xl font-bold tracking-tight mb-4">Corporate Identity</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-xs">Logo</Label>
          <div className="flex items-start gap-3 mt-1">
            <div className="w-20 h-20 rounded-md border border-black/10 bg-zinc-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {fullLogoUrl ? (
                <img src={fullLogoUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="brand-logo-preview" />
              ) : (
                <span className="text-[10px] text-zinc-400 uppercase tracking-widest">Logo</span>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input
                ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={onLogoFile} className="hidden" data-testid="brand-logo-file"
              />
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-md h-9 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="brand-logo-upload">
                  {uploading ? "Lädt…" : "Logo hochladen"}
                </Button>
                {b.logo_url && (
                  <Button type="button" size="sm" variant="ghost" className="rounded-md h-9 text-xs text-red-600 hover:bg-red-50" onClick={() => setB({ ...b, logo_url: "" })} data-testid="brand-logo-remove">
                    Entfernen
                  </Button>
                )}
              </div>
              <Input
                className="h-9 rounded-md text-xs"
                placeholder="…oder Logo-URL einfügen"
                value={b.logo_url || ""}
                onChange={(e) => setB({ ...b, logo_url: e.target.value })}
                data-testid="brand-logo"
              />
              {uploadErr && <p className="text-xs text-red-600" data-testid="brand-logo-error">{uploadErr}</p>}
              <p className="text-[10px] text-zinc-400">PNG, JPG, WebP oder SVG · max. 4 MB</p>
            </div>
          </div>
        </div>
        <div className="col-span-2"><Label className="text-xs">Tagline</Label><Input className="h-11 rounded-none" value={b.tagline || ""} onChange={(e) => setB({ ...b, tagline: e.target.value })} data-testid="brand-tagline" /></div>
        <div><Label className="text-xs">Primärfarbe</Label><Input type="color" className="h-11 rounded-none p-1" value={b.primary_color || "#2563EB"} onChange={(e) => setB({ ...b, primary_color: e.target.value })} data-testid="brand-primary" /></div>
        <div><Label className="text-xs">Sekundärfarbe</Label><Input type="color" className="h-11 rounded-none p-1" value={b.secondary_color || "#0EA5E9"} onChange={(e) => setB({ ...b, secondary_color: e.target.value })} data-testid="brand-secondary" /></div>
        <div className="col-span-2">
          <Label className="text-xs">Schriftart (Google Fonts)</Label>
          <Input
            list="popular-google-fonts"
            className="h-11 rounded-md mt-1"
            placeholder="z. B. Outfit, Poppins, Roboto…"
            value={b.font_family || ""}
            onChange={(e) => setB({ ...b, font_family: e.target.value })}
            data-testid="brand-font"
          />
          <datalist id="popular-google-fonts">
            {POPULAR_FONTS.map((f) => <option key={f} value={f} />)}
          </datalist>
          <p className="text-[10px] text-zinc-400 mt-1">
            Jeder Schriftname aus <a href="https://fonts.google.com" target="_blank" rel="noreferrer" className="underline">fonts.google.com</a> funktioniert — wird live in der Vorschau geladen.
          </p>
        </div>
      </div>
      <Button className="mt-6 rounded-none bg-black text-white hover:bg-zinc-800" onClick={save} data-testid="brand-save">Speichern</Button>
      <div className="mt-6 p-4 border border-dashed border-black/20" data-testid="brand-preview">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Vorschau</div>
        <div style={{ fontFamily: `"${b.font_family || "Outfit"}", sans-serif` }} className="space-y-2">
          <div className="flex items-center gap-3 mb-3">
            {fullLogoUrl && <img src={fullLogoUrl} alt="" className="h-10 w-auto object-contain" />}
            <div className="font-bold text-2xl tracking-tight" style={{ color: b.primary_color }}>{tenant.name}</div>
          </div>
          <div className="text-sm" style={{ color: "#666" }}>{b.tagline}</div>
          <div className="flex gap-2">
            <button className="px-4 py-2 text-white text-sm font-medium" style={{ background: b.primary_color }}>Jetzt buchen</button>
            <button className="px-4 py-2 text-sm border" style={{ borderColor: b.secondary_color, color: b.secondary_color }}>Mehr</button>
          </div>
        </div>
      </div>
    </div>
  );
}


const ROLE_LABEL = {
  tenant_owner: { de: "Admin", color: "#1d6781", desc: "Voller Zugriff: Stammdaten, Team, Abrechnung, Branding" },
  tenant_staff: { de: "Disposition", color: "#2A86A8", desc: "Kommerziell: Kunden, Abos, Touren, Rechnungen" },
  tenant_driver: { de: "Fahrer", color: "#3FA64E", desc: "Mobile Tour-Ansicht & Status-Updates" },
};

function TeamPanel({ currentUser }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: "tenant_staff" });
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try { const r = await api.get("/team"); setMembers(r.data); } catch (e) { console.error(e); }
  };
  useEffect(() => { refresh(); }, []);

  const canManage = currentUser.role === "tenant_owner" || currentUser.role === "platform_admin";

  const invite = async () => {
    try {
      await api.post("/team", f);
      setOpen(false);
      setF({ email: "", name: "", role: "tenant_staff" });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const changeRole = async (user_id, role) => {
    try { await api.patch(`/team/${user_id}`, { role }); refresh(); } catch (e) { alert(e.message); }
  };

  const remove = async (user_id) => {
    if (!confirm("Mitglied entfernen? Der Zugriff auf diesen Mandanten wird entzogen.")) return;
    try { await api.delete(`/team/${user_id}`); refresh(); } catch (e) { alert(e.message); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 bg-white border border-[var(--tonneva-border)] rounded-2xl">
        <div className="px-5 py-3 border-b border-[var(--tonneva-border)] flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Team & Rollen ({members.length})</h3>
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full bg-tonneva-gradient text-white hover:opacity-90" data-testid="invite-member-btn">
                  <Plus size={12} weight="bold" className="mr-1" /> Mitglied einladen
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader><DialogTitle className="font-heading text-xl">Neues Team-Mitglied</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">E-Mail (Google)</Label><Input className="h-11 rounded-md" placeholder="kollegin@firma.de" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="member-email" /></div>
                  <div><Label className="text-xs">Name</Label><Input className="h-11 rounded-md" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="member-name" /></div>
                  <div>
                    <Label className="text-xs">Rolle</Label>
                    <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                      <SelectTrigger className="rounded-md h-11" data-testid="member-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tenant_owner">Admin — voller Zugriff</SelectItem>
                        <SelectItem value="tenant_staff">Disposition — kaufmännisch</SelectItem>
                        <SelectItem value="tenant_driver">Fahrer — mobile Tour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-zinc-500 p-3 rounded-md" style={{ background: "var(--tonneva-gradient-soft)" }}>
                    Das Mitglied erhält Zugriff, sobald es sich erstmals mit dieser E-Mail per Google anmeldet.
                  </div>
                  <Button onClick={invite} className="w-full rounded-full bg-tonneva-gradient text-white hover:opacity-90" data-testid="submit-invite">Einladen</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-zinc-500">
            <tr><th className="px-5 py-2">Mitglied</th><th className="px-5 py-2">Rolle</th><th className="px-5 py-2">Status</th><th className="px-5 py-2"></th></tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-zinc-400">Noch keine Mitglieder</td></tr>
            ) : members.map((m) => {
              const rl = ROLE_LABEL[m.role] || { de: m.role, color: "#999" };
              return (
                <tr key={m.user_id} className="border-t border-[var(--tonneva-border)]" data-testid={`team-row-${m.user_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      {m.picture ? <img src={m.picture} alt="" className="w-7 h-7 rounded-full" /> : <UserCircle size={28} weight="duotone" className="text-zinc-400" />}
                      <div>
                        <div className="font-medium text-sm">{m.name}</div>
                        <div className="text-xs text-zinc-500">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {canManage && editing === m.user_id ? (
                      <Select value={m.role} onValueChange={(v) => { changeRole(m.user_id, v); setEditing(null); }}>
                        <SelectTrigger className="h-8 rounded-md text-xs w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tenant_owner">Admin</SelectItem>
                          <SelectItem value="tenant_staff">Disposition</SelectItem>
                          <SelectItem value="tenant_driver">Fahrer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold text-white" style={{ background: rl.color }}>
                        {rl.de}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{m.invited ? "Eingeladen" : "Aktiv"}</td>
                  <td className="px-5 py-3 text-right">
                    {canManage && (
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => setEditing(editing === m.user_id ? null : m.user_id)} className="p-1.5 hover:bg-zinc-100 rounded-md" data-testid={`edit-member-${m.user_id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => remove(m.user_id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-md" data-testid={`remove-member-${m.user_id}`}>
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-[var(--tonneva-border)] rounded-2xl p-6">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-4">Rollen-Übersicht</h3>
        <div className="space-y-3">
          {Object.entries(ROLE_LABEL).map(([k, v]) => (
            <div key={k} className="flex gap-3 p-3 rounded-lg" style={{ background: "var(--tonneva-gradient-soft)" }}>
              <div className="w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ background: v.color }}>{v.de[0]}</div>
              <div>
                <div className="text-sm font-semibold">{v.de}</div>
                <div className="text-xs text-zinc-500">{v.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NotificationBell({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (e) { console.warn("[Notifications] load failed", e); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markRead = async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      await load();
    } catch (e) { console.warn("[Notifications] markRead failed", e); }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      await load();
    } catch (e) { console.warn("[Notifications] markAll failed", e); }
  };

  const renderMsg = (n) => {
    if (n.type === "hot_cluster_reached") {
      const p = n.payload || {};
      return `🔥 ${p.city || "Ein Ort"} hat gerade den ${p.count}. Interessenten — Sondertour einplanen?`;
    }
    return "Neue Nachricht";
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center gap-1.5 text-zinc-600 hover:text-[var(--tonneva-primary)] p-1"
        data-testid="notification-bell"
        aria-label={`${unread} ungelesene Benachrichtigungen`}
      >
        <Bell size={16} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-30 w-80 bg-white border border-black/10 shadow-xl rounded-md overflow-hidden" data-testid="notification-panel">
            <div className="px-3 py-2 border-b border-black/10 flex items-center justify-between bg-zinc-50">
              <div className="text-xs font-semibold uppercase tracking-widest">Benachrichtigungen</div>
              {unread > 0 && (
                <button onClick={markAll} className="text-[10px] text-zinc-500 hover:text-black underline" data-testid="notif-mark-all-read">Alle gelesen</button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-xs text-zinc-400">Keine Benachrichtigungen</div>
              ) : items.map((n) => (
                <div key={n.notification_id} className={`p-3 border-b border-black/5 text-xs ${n.read ? "bg-white" : "bg-orange-50"}`} data-testid={`notif-${n.notification_id}`}>
                  <div className="leading-relaxed">{renderMsg(n)}</div>
                  <div className="mt-1.5 flex items-center justify-between text-[10px] text-zinc-500">
                    <span>{new Date(n.created_at).toLocaleString("de-DE")}</span>
                    <div className="flex gap-2">
                      {n.type === "hot_cluster_reached" && (
                        <button
                          onClick={() => { markRead(n.notification_id); setOpen(false); onNavigate("leads"); }}
                          className="text-orange-700 font-semibold hover:underline"
                          data-testid={`notif-goto-leads-${n.notification_id}`}
                        >Zu Interessenten →</button>
                      )}
                      {!n.read && (
                        <button onClick={() => markRead(n.notification_id)} className="text-zinc-500 hover:text-black" data-testid={`notif-read-${n.notification_id}`}>Gelesen</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

